/**
 * useMmsMapData Hook
 * 
 * Manages MMS (Model Management System) data fetching, parsing, and state.
 * Extracted from the standalone MMS page for reuse in Dashboard MMS mode.
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import type { AppDispatch } from '@/redux/store';
import HydraulicSimulatorService from '@/domain/HydraulicSimulator/HydraulicSimulatorService';
import { setShowLoader, setLoaderText } from '@/redux/slices/loaderSlice';
import { notification } from 'antd';

const hydraulicSimulatorService = new HydraulicSimulatorService();

// Types
export interface MmsNode {
	id: string;
	assetId: string;
	type: string;
	longitude: number;
	latitude: number;
	edited?: boolean;
	invisible?: boolean;
	attributes?: any[];
	[key: string]: any;
}

export interface MmsLine {
	id: string;
	assetId: string;
	type: string;
	sourceNodeId: string;
	targetNodeId: string;
	coordinates: [number, number][];
	edited?: boolean;
	invisible?: boolean;
	attributes?: any[];
	[key: string]: any;
}

export interface MmsAssetData {
	mmsId: string;
	assets: any[];
}

export interface UseMmsMapDataProps {
	mmsId?: string;
	fileName?: string;
	locationId: string;
	enabled: boolean;
}

export interface UseMmsMapDataResult {
	allNodes: MmsNode[];
	allLines: MmsLine[];
	assetsData: MmsAssetData | null;
	originalAssets: any[];
	isLoading: boolean;
	nodeMap: Map<string, MmsNode>;
	dataCenter: { latitude: number; longitude: number } | null;
	// Mutation functions
	updateNodePosition: (nodeId: string, lng: number, lat: number) => void;
	updateAssetAttribute: (assetId: string, attributeName: string, value: any) => void;
	setAssetAttributes: (assetId: string, attributes: any[]) => void;
	markAssetDeleted: (assetId: string) => void;
	restoreAsset: (assetId: string) => void;
	saveChanges: () => Promise<void>;
	refreshData: () => void;
}

// Helper: Validate coordinates
const isValidCoordinate = (lng: number, lat: number): boolean => {
	return (
		typeof lng === 'number' &&
		typeof lat === 'number' &&
		lng >= -180 &&
		lng <= 180 &&
		lat >= -90 &&
		lat <= 90
	);
};

// Helper: Calculate geographic center
const calculateDataCenter = (nodes: MmsNode[], lines: MmsLine[]) => {
	let minLat = Infinity, maxLat = -Infinity;
	let minLng = Infinity, maxLng = -Infinity;

	nodes.forEach((node) => {
		if (node.latitude && node.longitude) {
			minLat = Math.min(minLat, node.latitude);
			maxLat = Math.max(maxLat, node.latitude);
			minLng = Math.min(minLng, node.longitude);
			maxLng = Math.max(maxLng, node.longitude);
		}
	});

	lines.forEach((line) => {
		if (line.coordinates) {
			line.coordinates.forEach((coord) => {
				minLat = Math.min(minLat, coord[1]);
				maxLat = Math.max(maxLat, coord[1]);
				minLng = Math.min(minLng, coord[0]);
				maxLng = Math.max(maxLng, coord[0]);
			});
		}
	});

	if (minLat === Infinity || maxLat === -Infinity) {
		return null;
	}

	return {
		latitude: (minLat + maxLat) / 2,
		longitude: (minLng + maxLng) / 2,
	};
};

/**
 * Hook to manage MMS map data
 */
export function useMmsMapData({
	mmsId,
	fileName,
	locationId,
	enabled,
}: UseMmsMapDataProps): UseMmsMapDataResult {
	const [allNodes, setAllNodes] = useState<MmsNode[]>([]);
	const [allLines, setAllLines] = useState<MmsLine[]>([]);
	const [assetsData, setAssetsData] = useState<MmsAssetData | null>(null);
	const [originalAssets, setOriginalAssets] = useState<any[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [dataCenter, setDataCenter] = useState<{ latitude: number; longitude: number } | null>(null);
	const dispatch = useDispatch<AppDispatch>();

	// Track if data has been loaded for current mmsId
	const loadedMmsIdRef = useRef<string | undefined>(undefined);

	// Memoized node lookup map
	const nodeMap = useMemo(() => {
		const map = new Map<string, MmsNode>();
		allNodes.forEach((node) => map.set(node.id, node));
		return map;
	}, [allNodes]);

	/**
	 * Parse raw asset data into nodes and lines
	 */
	const parseMapData = useCallback((resData: any) => {
		const nodes: MmsNode[] = [];
		const lines: MmsLine[] = [];

		if (!resData?.assets) return { nodes, lines };

		const pointTypes = ['junctions', 'outfalls', 'storage', 'junction', 'outfall'];
		const lineTypes = ['weirs', 'conduits', 'weir', 'conduit', 'pipes', 'pipe', 'orifices', 'pumps', 'pump'];

		resData.assets.forEach((asset: any) => {
			const resolvedType = (asset.infrastructureType || asset.type)?.toLowerCase();

			// Prioritize explicit assetType from API — it's authoritative
			const isExplicitLink = asset.assetType === 'LINK';
			const isExplicitNode = asset.assetType === 'NODE';

			const shouldBeLink = isExplicitLink || (!isExplicitNode && lineTypes.includes(resolvedType));
			const shouldBeNode = !shouldBeLink && (isExplicitNode || pointTypes.includes(resolvedType));

			if (shouldBeNode && asset.coordinates && asset.coordinates.length > 0) {
				const [lng, lat] = asset.coordinates[0];
				if (isValidCoordinate(lng, lat)) {
					nodes.push({
						id: asset.assetId,
						assetId: asset.assetId,
						type: asset.infrastructureType || asset.type || 'junction',
						label: asset.assetId,
						longitude: lng,
						latitude: lat,
						edited: asset.edited || false,
						invisible: asset.invisible || false,
						attributes: asset.attributes || [],
						...asset,
					});
				}
			} else if (shouldBeLink) {
				const sourceNodeId = asset.node1 || '';
				const targetNodeId = asset.node2 || '';
				lines.push({
					id: asset.assetId,
					assetId: asset.assetId,
					type: asset.infrastructureType || asset.type || 'conduit',
					sourceNodeId,
					targetNodeId,
					coordinates: asset.coordinates || [],
					edited: asset.edited || false,
					invisible: asset.invisible || false,
					attributes: asset.attributes || [],
					...asset,
				});
			}
		});

		return { nodes, lines };
	}, []);

	/**
	 * Fetch model data from API
	 */
	const getModelData = useCallback(async (modelId: string, modelFileName: string) => {
		if (!modelId || !locationId) return;

		setIsLoading(true);
		dispatch(setLoaderText('Loading...'));
		dispatch(setShowLoader(true));
		try {
			const response = await hydraulicSimulatorService.getAssetsFromMmsid(modelId, Number(locationId));
			
			if (response) {
				const { nodes, lines } = parseMapData(response);
				setAllNodes(nodes);
				setAllLines(lines);
				setAssetsData(response);
				setOriginalAssets(response.assets || []);
				setDataCenter(calculateDataCenter(nodes, lines));
				loadedMmsIdRef.current = modelId;
			}
		} catch (error) {
			console.error('Error fetching MMS model data:', error);
			notification.error({
				message: 'Error',
				description: 'Failed to load model data',
			});
		} finally {
			setIsLoading(false);
			dispatch(setShowLoader(false));
		}
	}, [locationId, parseMapData, dispatch]);

	/**
	 * Refresh data (re-fetch current model)
	 */
	const refreshData = useCallback(() => {
		if (mmsId && fileName) {
			getModelData(mmsId, fileName);
		}
	}, [mmsId, fileName, getModelData]);

	/**
	 * Update node position (for drag operations)
	 */
	const updateNodePosition = useCallback((nodeId: string, lng: number, lat: number) => {
		// Update nodes
		setAllNodes((prevNodes) => {
			const updatedNodes = prevNodes.map((node) =>
				node.id === nodeId
					? { ...node, longitude: lng, latitude: lat, edited: true }
					: node
			);

			// Update connected lines
			setAllLines((prevLines) =>
				prevLines.map((line) => {
					if (line.sourceNodeId === nodeId) {
						// Node is the source - update start of line
						const targetNode = updatedNodes.find((n) => n.id === line.targetNodeId);
						if (targetNode) {
							return {
								...line,
								coordinates: [
									[lng, lat],
									[targetNode.longitude, targetNode.latitude],
								],
							};
						}
					} else if (line.targetNodeId === nodeId) {
						// Node is the target - update end of line
						const sourceNode = updatedNodes.find((n) => n.id === line.sourceNodeId);
						if (sourceNode) {
							return {
								...line,
								coordinates: [
									[sourceNode.longitude, sourceNode.latitude],
									[lng, lat],
								],
							};
						}
					}
					return line;
				})
			);

			return updatedNodes;
		});

		// Also update in assetsData
		setAssetsData((prev) => {
			if (!prev) return prev;
			return {
				...prev,
				assets: prev.assets.map((asset) =>
					asset.assetId === nodeId
						? { ...asset, coordinates: [[lng, lat]], edited: true }
						: asset
				),
			};
		});
	}, []);

	/**
	 * Update asset attribute value
	 */
	const updateAssetAttribute = useCallback((assetId: string, attributeName: string, value: any) => {
		setAssetsData((prev) => {
			if (!prev) return prev;
			return {
				...prev,
				assets: prev.assets.map((asset) => {
					if (asset.assetId === assetId) {
						return {
							...asset,
							edited: true,
							attributes: asset.attributes?.map((attr: any) =>
								attr.name === attributeName ? { ...attr, value } : attr
							),
						};
					}
					return asset;
				}),
			};
		});

		// Update in nodes/lines arrays
		setAllNodes((prev) =>
			prev.map((node) => {
				if (node.assetId === assetId) {
					return {
						...node,
						edited: true,
						attributes: node.attributes?.map((attr: any) =>
							attr.name === attributeName ? { ...attr, value } : attr
						),
					};
				}
				return node;
			})
		);

		setAllLines((prev) =>
			prev.map((line) => {
				if (line.assetId === assetId) {
					return {
						...line,
						edited: true,
						attributes: line.attributes?.map((attr: any) =>
							attr.name === attributeName ? { ...attr, value } : attr
						),
					};
				}
				return line;
			})
		);
	}, []);

	/**
	 * Mark asset as deleted (invisible)
	 */
	const markAssetDeleted = useCallback((assetId: string) => {
		setAssetsData((prev) => {
			if (!prev) return prev;
			return {
				...prev,
				assets: prev.assets.map((asset) =>
					asset.assetId === assetId ? { ...asset, invisible: true, edited: true } : asset
				),
			};
		});

		setAllNodes((prev) =>
			prev.map((node) =>
				node.assetId === assetId ? { ...node, invisible: true, edited: true } : node
			)
		);

		setAllLines((prev) =>
			prev.map((line) =>
				line.assetId === assetId ? { ...line, invisible: true, edited: true } : line
			)
		);
	}, []);

	/**
	 * Restore deleted asset
	 */
	const restoreAsset = useCallback((assetId: string) => {
		setAssetsData((prev) => {
			if (!prev) return prev;
			return {
				...prev,
				assets: prev.assets.map((asset) =>
					asset.assetId === assetId ? { ...asset, invisible: false, edited: true } : asset
				),
			};
		});

		setAllNodes((prev) =>
			prev.map((node) =>
				node.assetId === assetId ? { ...node, invisible: false, edited: true } : node
			)
		);

		setAllLines((prev) =>
			prev.map((line) =>
				line.assetId === assetId ? { ...line, invisible: false, edited: true } : line
			)
		);
	}, []);

	/**
	 * Save changes to backend
	 */
	const saveChanges = useCallback(async () => {
		if (!assetsData || !mmsId || !locationId) {
			notification.warning({
				message: 'No Model Selected',
				description: 'Load a model before saving changes.',
			});
			return;
		}

		// Build transformed payload — matches original Mms/index.jsx saveChanges logic
		const dataToSend = assetsData.assets
			?.filter((asset: any) => asset.invisible || asset.edited)
			.map((asset: any) => {
				const coordinates =
					asset.coordinates?.[0]?.[1] && asset.coordinates?.[0]?.[0] && asset.type !== 'conduits'
						? [
								{ type: 'coordinates', name: 'x', value: asset.coordinates[0][0].toString() },
								{ type: 'coordinates', name: 'y', value: asset.coordinates[0][1].toString() },
						  ]
						: [];
				return {
					assetId: asset.assetId,
					action: asset.edited ? 'UPDATE' : asset.invisible ? 'DELETE' : '',
					type: asset.type,
					attributeDTOList: [
						...coordinates,
						...(asset.attributes
							? asset.attributes
									.filter((at: any) => at.name !== 'name')
									.map((at: any) => ({
										name: at.name,
										value: at.value?.toString() ?? '',
									}))
							: []),
					],
				};
			});

		if (!dataToSend || dataToSend.length === 0) {
			notification.warning({
				message: 'No Changes',
				description: 'No coordinates or attributes were modified. Nothing to save.',
			});
			return;
		}

		console.log(`💾 Saving ${dataToSend.length} modified assets`);

		setIsLoading(true);
		try {
			const response = await hydraulicSimulatorService.postSaveChangesModelFile(
				mmsId,
				dataToSend,
				Number(locationId),
			);

			if (response) {
				notification.success({
					message: 'Successful',
					description: `Model saved successfully. New version: ${String(response).substring(0, 8)}`,
				});
				// Re-fetch with the new model id
				await getModelData(String(response), fileName || '');
			} else {
				// Fallback: clear edited flags without re-fetch
				setAssetsData((prev) => {
					if (!prev) return prev;
					return {
						...prev,
						assets: prev.assets
							.map((asset: any) => ({ ...asset, edited: false }))
							.filter((asset: any) => !asset.invisible),
					};
				});
				notification.success({
					message: 'Successful',
					description: 'Model File Edited successfully',
				});
			}
		} catch (error) {
			console.error('Error saving changes:', error);
			notification.error({
				message: 'Error',
				description: 'Failed to save changes',
			});
		} finally {
			setIsLoading(false);
		}
	}, [assetsData, mmsId, locationId, fileName, getModelData]);

	/**
	 * Replace the full attributes array for an asset (used when attributes are loaded lazily from the API)
	 */
	const setAssetAttributes = useCallback((assetId: string, attributes: any[]) => {
		setAssetsData((prev) => {
			if (!prev) return prev;
			return {
				...prev,
				assets: prev.assets.map((asset) =>
					asset.assetId === assetId ? { ...asset, attributes } : asset
				),
			};
		});
		setAllNodes((prev) =>
			prev.map((node) =>
				node.assetId === assetId ? { ...node, attributes } : node
			)
		);
		setAllLines((prev) =>
			prev.map((line) =>
				line.assetId === assetId ? { ...line, attributes } : line
			)
		);
	}, []);

	/**
	 * Effect: Load data when mmsId changes
	 */
	useEffect(() => {
		if (enabled && mmsId && mmsId !== loadedMmsIdRef.current) {
			getModelData(mmsId, fileName || '');
		}
	}, [enabled, mmsId, fileName, getModelData]);

	/**
	 * Effect: Clear data when disabled
	 */
	useEffect(() => {
		if (!enabled) {
			setAllNodes([]);
			setAllLines([]);
			setAssetsData(null);
			setOriginalAssets([]);
			loadedMmsIdRef.current = undefined;
		}
	}, [enabled]);

	return {
		allNodes,
		allLines,
		assetsData,
		originalAssets,
		isLoading,
		nodeMap,
		dataCenter,
		updateNodePosition,
		updateAssetAttribute,
		setAssetAttributes,
		markAssetDeleted,
		restoreAsset,
		saveChanges,
		refreshData,
	};
}

export default useMmsMapData;
